import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
    BarChart3, Download, Users, Eye, Clock, UserPlus, Heart, Boxes,
    TrendingUp, Activity, Layers, Bell, Send, RotateCcw,
} from "lucide-react";
import {
    ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis,
    CartesianGrid, Tooltip,
} from "recharts";
import { getReportsOverview, downloadReportsPdf, api } from "../../lib/api";
import { Loader } from "../Loader";

const PURPLE = "#9D4CDD";

const fmtSecs = (s) => {
    s = Math.round(s || 0);
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    if (h) return `${h}h ${m}m`;
    if (m) return `${m}m ${sec}s`;
    return `${sec}s`;
};
const prettyPath = (p) => {
    if (!p || p === "/") return "Home";
    return p.replace(/^\//, "").split("/").map((x) => x.replace(/-/g, " "))
        .join(" › ").replace(/\b\w/g, (c) => c.toUpperCase());
};
const shortDay = (d) => (d || "").slice(5); // MM-DD

const Panel = ({ title, icon: Icon, children, right, testid }) => (
    <section className="rounded-2xl border border-white/10 bg-[#12141D]/70 p-4 sm:p-5" data-testid={testid}>
        <div className="flex items-center justify-between gap-3 mb-4">
            <h3 className="flex items-center gap-2 text-sm font-black uppercase tracking-wider text-white">
                {Icon && <Icon className="w-4 h-4 text-[#C4A5F4]" />} {title}
            </h3>
            {right}
        </div>
        {children}
    </section>
);

const Kpi = ({ label, value, icon: Icon, testid }) => (
    <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#9D4CDD]/12 to-transparent px-4 py-3" data-testid={testid}>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-white/45 font-bold mb-1">
            {Icon && <Icon className="w-3.5 h-3.5 text-[#C4A5F4]" />} {label}
        </div>
        <div className="text-2xl font-black text-white leading-none">{value}</div>
    </div>
);

const chartTip = {
    contentStyle: { background: "#0E0F16", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, fontSize: 12 },
    labelStyle: { color: "#C4A5F4", fontWeight: 700 },
    itemStyle: { color: "#fff" },
};

function BarList({ rows, maxKey, labelKey, valueFmt, testidPrefix }) {
    const max = Math.max(1, ...rows.map((r) => r[maxKey] || 0));
    return (
        <div className="space-y-1.5">
            {rows.map((r, i) => (
                <div key={i} className="flex items-center gap-3" data-testid={`${testidPrefix}-${i}`}>
                    <div className="w-40 sm:w-56 shrink-0 truncate text-xs text-white/80">{r._label}</div>
                    <div className="flex-1 h-4 rounded-full bg-white/[0.05] overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${((r[maxKey] || 0) / max) * 100}%`, background: `linear-gradient(90deg, ${PURPLE}, #6D3AA6)` }} />
                    </div>
                    <div className="w-24 shrink-0 text-right font-mono-stat text-[11px] text-white/70">{valueFmt(r)}</div>
                </div>
            ))}
        </div>
    );
}

export default function Reports() {
    const [downloading, setDownloading] = useState(false);
    const { data, isLoading, isError } = useQuery({ queryKey: ["reports-overview"], queryFn: getReportsOverview });

    const doDownload = async () => {
        setDownloading(true);
        try { await downloadReportsPdf(); toast.success("Report downloaded"); }
        catch { toast.error("Could not generate PDF"); }
        finally { setDownloading(false); }
    };

    if (isLoading) return <Loader />;
    if (isError || !data) return <p className="text-sm text-white/50" data-testid="reports-error">Could not load reports.</p>;

    const u = data.users, a = data.activity, f = data.following;
    const roleRows = Object.entries(u.by_role || {}).sort((x, y) => y[1] - x[1])
        .map(([k, v]) => ({ _label: k[0].toUpperCase() + k.slice(1), n: v }));
    const dau = (a.dau_by_day || []).map((r) => ({ ...r, d: shortDay(r.date) }));
    const signups = (u.signups_by_day || []).map((r) => ({ ...r, d: shortDay(r.date) }));
    const anyDau = dau.some((r) => r.users || r.views || r.viewers);
    const anySignup = signups.some((r) => r.count);
    const topPages = (data.top_pages || []).slice(0, 15).map((p) => ({ ...p, _label: prettyPath(p.path) }));
    const myTabs = (data.my_tabs || []).map((p) => ({ ...p, _label: prettyPath(p.path) }));
    const content = Object.entries(data.content || {});

    return (
        <div className="space-y-5" data-testid="reports-page">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="flex items-center gap-2 text-2xl font-black text-white">
                        <BarChart3 className="w-6 h-6 text-[#C4A5F4]" /> Reports
                    </h2>
                    <p className="text-xs text-white/45 mt-0.5">Owner analytics · generated {String(data.generated_at).slice(0, 16).replace("T", " ")} UTC</p>
                </div>
                <button onClick={doDownload} disabled={downloading} data-testid="reports-download-pdf"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#9D4CDD] hover:bg-[#B366F0] disabled:opacity-50 text-white font-bold uppercase tracking-wider text-xs transition-colors">
                    <Download className="w-4 h-4" /> {downloading ? "Preparing…" : "Download PDF"}
                </button>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="reports-kpis">
                <Kpi label="Total Users" value={u.total} icon={Users} testid="kpi-total-users" />
                <Kpi label="Unique Viewers" value={(a.unique_viewers || 0).toLocaleString()} icon={Eye} testid="kpi-unique-viewers" />
                <Kpi label="Tracked Users" value={a.tracked_users} icon={Activity} testid="kpi-tracked" />
                <Kpi label="Signups (30d)" value={u.signups_30d} icon={UserPlus} testid="kpi-signups" />
                <Kpi label="Total Views" value={a.total_views.toLocaleString()} icon={Eye} testid="kpi-views" />
                <Kpi label="Total Time" value={a.total_time} icon={Clock} testid="kpi-time" />
            </div>

            {/* Charts */}
            <div className="grid lg:grid-cols-2 gap-5">
                <Panel title="Daily Active — Viewers vs Users (30d)" icon={TrendingUp} testid="reports-dau"
                    right={<span className="flex items-center gap-3 text-[10px] uppercase tracking-wider font-bold">
                        <span className="flex items-center gap-1 text-white/55"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: "#5AA9FF" }} /> Viewers</span>
                        <span className="flex items-center gap-1 text-white/55"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: PURPLE }} /> Users</span>
                    </span>}>
                    {anyDau ? (
                        <ResponsiveContainer width="100%" height={220}>
                            <AreaChart data={dau} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="dauFill" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={PURPLE} stopOpacity={0.5} />
                                        <stop offset="100%" stopColor={PURPLE} stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="viewFill" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#5AA9FF" stopOpacity={0.35} />
                                        <stop offset="100%" stopColor="#5AA9FF" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke="#ffffff10" vertical={false} />
                                <XAxis dataKey="d" tick={{ fill: "#ffffff55", fontSize: 10 }} interval="preserveStartEnd" />
                                <YAxis allowDecimals={false} tick={{ fill: "#ffffff55", fontSize: 10 }} />
                                <Tooltip {...chartTip} />
                                <Area type="monotone" dataKey="viewers" name="Unique viewers" stroke="#5AA9FF" strokeWidth={2} fill="url(#viewFill)" />
                                <Area type="monotone" dataKey="users" name="Registered users" stroke={PURPLE} strokeWidth={2} fill="url(#dauFill)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : <EmptyNote text="Daily-active tracking has started. Once people visit (October onward), this chart fills in." />}
                </Panel>

                <Panel title="New Signups (30d)" icon={UserPlus} testid="reports-signups">
                    {anySignup ? (
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={signups} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
                                <CartesianGrid stroke="#ffffff10" vertical={false} />
                                <XAxis dataKey="d" tick={{ fill: "#ffffff55", fontSize: 10 }} interval="preserveStartEnd" />
                                <YAxis allowDecimals={false} tick={{ fill: "#ffffff55", fontSize: 10 }} />
                                <Tooltip {...chartTip} cursor={{ fill: "#ffffff08" }} />
                                <Bar dataKey="count" name="New users" fill={PURPLE} radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : <EmptyNote text="No new signups in the last 30 days yet." />}
                </Panel>
            </div>

            {/* Roles + Sitewide pages */}
            <div className="grid lg:grid-cols-2 gap-5">
                <Panel title="Users by Role" icon={Users} testid="reports-roles">
                    <BarList rows={roleRows} maxKey="n" testidPrefix="role-row" valueFmt={(r) => r.n} />
                </Panel>
                <Panel title="Most-Visited Pages (sitewide)" icon={Eye} testid="reports-top-pages">
                    {topPages.length ? <BarList rows={topPages} maxKey="seconds" testidPrefix="top-page"
                        valueFmt={(r) => `${r.views} · ${fmtSecs(r.seconds)}`} /> : <EmptyNote text="No page-view data yet." />}
                </Panel>
            </div>

            {/* My tabs — merged Recent Activity, EVERY tab */}
            <Panel title="Your Tabs — every page you've visited" icon={Clock} testid="reports-my-tabs"
                right={<span className="text-[11px] text-white/50">{myTabs.length} pages · {data.my_total_time} total</span>}>
                {myTabs.length ? (
                    <div className="max-h-[420px] overflow-y-auto pr-1">
                        <BarList rows={myTabs} maxKey="seconds" testidPrefix="my-tab"
                            valueFmt={(r) => `${r.views}× · ${fmtSecs(r.seconds)}`} />
                    </div>
                ) : <EmptyNote text="No personal page-view history yet." />}
            </Panel>

            {/* Following */}
            <Panel title="Following & Favourites" icon={Heart} testid="reports-following">
                <div className="grid grid-cols-3 gap-3 mb-4">
                    <Kpi label="Total Follows" value={f.total} testid="kpi-fav-total" />
                    <Kpi label="Team Follows" value={f.teams_followed} testid="kpi-fav-teams" />
                    <Kpi label="Player Follows" value={f.players_followed} testid="kpi-fav-players" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-white/45 mb-2">Most-followed teams</div>
                        {f.top_teams?.length ? <BarList rows={f.top_teams.map((t) => ({ ...t, _label: t.abbr }))} maxKey="count" testidPrefix="fav-team" valueFmt={(r) => r.count} /> : <EmptyNote text="None yet." />}
                    </div>
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-white/45 mb-2">Most-followed players</div>
                        {f.top_players?.length ? <BarList rows={f.top_players.map((t) => ({ ...t, _label: t.name }))} maxKey="count" testidPrefix="fav-player" valueFmt={(r) => r.count} /> : <EmptyNote text="None yet." />}
                    </div>
                </div>
            </Panel>

            {/* Content inventory */}
            <Panel title="Content Inventory" icon={Boxes} testid="reports-content">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
                    {content.map(([k, v]) => (
                        <div key={k} className="rounded-xl border border-white/10 bg-black/25 px-3 py-2.5" data-testid={`content-${k.replace(/\s+/g, '-').toLowerCase()}`}>
                            <div className="text-xl font-black text-white leading-none">{Number(v).toLocaleString()}</div>
                            <div className="text-[10px] uppercase tracking-wider text-white/45 font-bold mt-1 flex items-center gap-1"><Layers className="w-3 h-3" /> {k}</div>
                        </div>
                    ))}
                </div>
            </Panel>

            {/* Push subscribers */}
            <Panel title="Push Subscribers" icon={Bell} testid="reports-push-subs">
                <div className="grid grid-cols-3 gap-3 mb-4">
                    <Kpi label="Opted In" value={data.push?.opted_in ?? 0} icon={Bell} testid="kpi-push-optedin" />
                    <Kpi label="Follow PWHL" value={data.push?.by_league?.pwhl ?? 0} icon={Users} testid="kpi-push-pwhl" />
                    <Kpi label="Follow NHL" value={data.push?.by_league?.nhl ?? 0} icon={Users} testid="kpi-push-nhl" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-white/45 mb-2">Subscribers by PWHL team</div>
                        {data.push?.teams_pwhl?.length ? <BarList rows={data.push.teams_pwhl} maxKey="subs" testidPrefix="push-team-pwhl" valueFmt={(r) => r.subs} /> : <EmptyNote text="No team opt-ins yet." />}
                    </div>
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-white/45 mb-2">Subscribers by NHL team</div>
                        {data.push?.teams_nhl?.length ? <BarList rows={data.push.teams_nhl} maxKey="subs" testidPrefix="push-team-nhl" valueFmt={(r) => r.subs} /> : <EmptyNote text="No team opt-ins yet." />}
                    </div>
                </div>
            </Panel>

            {/* Push send history */}
            <Panel title="Push Send History" icon={Send} testid="reports-push-history">
                {data.push?.recent_sends?.length ? (
                    <div className="space-y-1.5">
                        {data.push.recent_sends.map((s) => <SendRow key={s.id} s={s} />)}
                    </div>
                ) : <EmptyNote text="No notifications sent yet." />}
            </Panel>
        </div>
    );
}

function SendRow({ s }) {
    const [busy, setBusy] = useState(false);
    const resend = async () => {
        setBusy(true);
        try {
            const r = await api.post(`/push/resend/${s.id}`).then((x) => x.data);
            if (r.sent) toast.success("Resent");
            else toast.error(r.reason || "No recipients");
        } catch (e) { toast.error(e?.response?.data?.detail || "Resend failed"); }
        setBusy(false);
    };
    return (
        <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-black/20 px-3 py-2" data-testid={`push-send-${s.id}`}>
            <div className="flex-1 min-w-0">
                <div className="text-xs font-bold text-white/90 truncate">{s.title}</div>
                <div className="text-[10px] text-white/40 truncate">{s.audience} · {s.recipients === -1 ? "all subs" : `${s.recipients} sent`} · {String(s.at).slice(5, 16).replace("T", " ")}{s.sent ? "" : " · failed"}</div>
            </div>
            <button onClick={resend} disabled={busy} data-testid={`push-resend-${s.id}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider bg-white/10 hover:bg-[#9D4CDD] text-white/80 hover:text-white disabled:opacity-40 shrink-0">
                <RotateCcw className="w-3 h-3" /> Resend
            </button>
        </div>
    );
}

const EmptyNote = ({ text }) => (
    <div className="flex items-center justify-center h-[120px] text-center text-xs text-white/40 px-4">{text}</div>
);
